import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-pricing-comparison';

/**
 * Pricing and plan comparison table benchmarking SignNow against competitors.
 * Header (heading + subtitle) above a multi-column table; column headers are company names and rows render labels, formatted entry prices, and G2 star ratings.
 * Tabular competitor comparison, unlike feature-grid or stat-card SignNow variants.
 *
 * @category SignNow
 * @slot heading - <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]>
 * @slot subtitle - <me-text variant="large">
 * @example
 *   <sn-pricing-comparison
 *     corner-label="Features"
 *     columns='[{"label":"SignNow"},{"label":"DocuSign"},{"label":"Adobe Sign"}]'
 *     rows='[{"label":"Entry Price","values":["from $8/mo","from $13/mo","from $15/mo"]},{"label":"G2 Rating","values":["4.6","4.4","4.3"]}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Pricing and Plan Comparison</me-heading>
 *     <me-text slot="subtitle" variant="large">All pricing shown in USD per user per month.</me-text>
 *   </sn-pricing-comparison>
 */
@Component({
  tag: 'sn-pricing-comparison',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcPricingComparison {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Text for the top-left corner cell (row 0, column 0) — the header cell that
   * sits above the row labels and left of the column (company) headers. Left
   * empty when omitted.
   */
  @Prop({ attribute: 'corner-label' }) cornerLabel?: string;

  /**
   * Table column header labels (company names).
   * Each item can be a plain string or an object with a `label` property.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Column header text (company name)
   * @item.required label
   * @item.content label
   * @minItems 4
   */
  @Prop({ mutable: true }) columns?: (string | { label: string })[] | string;

  /**
   * Table comparison rows.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Row label (first column)
   * @item.property {array} values - Cell values aligned to columns (a comma-separated string is also accepted)
   * @item.required label
   * @item.required values
   * @item.content label values
   * @minItems 4
   */
  @Prop({ mutable: true }) rows?: { label?: string; values?: string[] | string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `columns` / `rows` props.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} columns - Fallback columns (use columns prop instead)
   * @property {array} rows - Fallback rows (use rows prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        cornerLabel?: string;
        raw_html?: string;
        columns?: string[];
        rows?: { label?: string; values?: string[] | string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.columns = parseJsonProp(this.columns);
    this.rows = parseJsonProp(this.rows);
  }

  /**
   * Normalize a row's `values` into an array of cell strings. The block builder can
   * emit `values` as a comma-separated string (e.g. "from $8/mo,from $13/mo") instead
   * of a real array; a bare `Array.isArray` check would then treat it as empty and the
   * row would render no value cells (a header-only, "broken/empty" table). Accept both:
   * a real array is used as-is; a string is split on commas and trimmed.
   */
  private normalizeValues(values: string[] | string | undefined): string[] {
    if (Array.isArray(values)) return values;
    if (typeof values === 'string') {
      return values.split(',').map(v => v.trim());
    }
    return [];
  }

  private renderTableValue(rowLabel: string | undefined, val: string) {
    if (rowLabel?.toLowerCase() === 'entry price') {
      const normalized = val.replace(/^from\s+/i, '');
      const price = normalized.match(/^\$?\d+(?:\.\d+)?/)?.[0] || normalized;
      const suffix = normalized.replace(/^\$?\d+(?:\.\d+)?/, '');
      return (
        <span class={`${COMPONENT_NAME}__entry-price`}>
          <span>from</span>
          <span><strong>{price}</strong>{suffix}</span>
        </span>
      );
    }
    return val;
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const rawColumns = Array.isArray(this.columns)
      ? this.columns
      : (Array.isArray(values?.columns) ? values.columns : []);
    const columns = rawColumns.map((col: string | { label: string }) =>
      typeof col === 'string' ? col : col?.label ?? ''
    );

    const rows = Array.isArray(this.rows)
      ? this.rows
      : (Array.isArray(values?.rows) ? values.rows : []);

    const cornerLabel = this.cornerLabel ?? values?.cornerLabel;

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="column">
          <me-column spanLg="12">
            <div class={`${COMPONENT_NAME}__header`}>
              <slot name="heading">
                {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
              </slot>
              <slot name="subtitle">
                {values?.subtitle && <me-text variant="large">{values.subtitle}</me-text>}
              </slot>
            </div>
          </me-column>
          {(columns.length > 0 || rows.length > 0) && (
            <me-column spanLg="12">
              <div class={`${COMPONENT_NAME}__table-wrapper`}>
                <table class={`${COMPONENT_NAME}__table`}>
                  {columns.length > 0 && (
                    <thead>
                      <tr class={`${COMPONENT_NAME}__row ${COMPONENT_NAME}__row--header`}>
                        <th class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--header ${COMPONENT_NAME}__cell--label`}>{cornerLabel}</th>
                        {columns.map((col) => (
                          <th class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--header`}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {rows.map((row) => {
                      const vals = this.normalizeValues(row.values);
                      const isRating = row.label?.toLowerCase().includes('rating');
                      return (
                        <tr class={`${COMPONENT_NAME}__row`}>
                          <td class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--label`}>{row.label}</td>
                          {vals.map((val) => (
                            <td class={`${COMPONENT_NAME}__cell ${COMPONENT_NAME}__cell--value`}>
                              {isRating ? (
                                <span class={`${COMPONENT_NAME}__rating`}>
                                  <me-icon name="shapes--star" class={`${COMPONENT_NAME}__star`} width="16" height="16" lazy={false}></me-icon>
                                  {val}
                                </span>
                              ) : (
                                this.renderTableValue(row.label, val)
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </me-column>
          )}
        </me-row>
      </me-section>
    );
  }
}
